import { makeAutoObservable, runInAction } from 'mobx';
import { v4 as uuid } from 'uuid';

import { CartCalculationOrderResponse } from '~/api/Catalog';
import { DeliverySlot } from '~/api/ETA';
import { company } from '~/company/Company';
import Delivery from '~/stores/CheckoutStore/Delivery';
import { CheckoutStorePartial } from '~/stores/CheckoutStore/interfaces';
import { BaseStore } from '~/stores/interfaces/BaseStore';

class SlotSelectModalStore implements BaseStore {
  private readonly _checkoutStore: CheckoutStorePartial;

  private _selectedSlot: Delivery | null = null;
  private _selectedOrder: CartCalculationOrderResponse | null = null;

  private _selectedSlotIdForSplit: string | null = null;
  private _isAutomatic: boolean = true;

  private _isLoading: boolean = false;

  constructor(checkoutStore: CheckoutStorePartial) {
    this._checkoutStore = checkoutStore;

    makeAutoObservable(this);
  }

  get selectedSlot(): Delivery | null {
    return this._selectedSlot;
  }

  get selectedOrder() {
    return this._selectedOrder;
  }

  get selectedSlotIdForSplit() {
    return this._selectedSlotIdForSplit;
  }

  get isAutomatic() {
    return this._isAutomatic;
  }

  get isLoading() {
    return this._isLoading;
  }

  setSelectedSlot(
    slot: Delivery | null,
    order: CartCalculationOrderResponse | null,
  ): void {
    this._selectedSlot = slot;
    this._selectedOrder = order;
  }

  setSelectedSlotForSplit(slotId: string) {
    this._selectedSlotIdForSplit = slotId;
  }

  setIsAutomatic(isAutomatic: boolean) {
    this._isAutomatic = isAutomatic;
  }

  setIsLoading(isLoading: boolean) {
    this._isLoading = isLoading;
  }

  resetSelectedSlotForSplit() {
    this._selectedSlotIdForSplit = null;
  }

  removeAutomaticDelivery(selectedSlotId?: string) {
    const { deliveries } = this._checkoutStore;
    const currentDelivery = this.selectedSlot;

    if (!deliveries || !currentDelivery) {
      throw new Error('Something went wrong');
    }

    const newDeliveries = deliveries.map((delivery) => {
      if (delivery.slotDeliveryDetails?.scheduleSlotId === selectedSlotId) {
        return {
          ...delivery,
          slotDeliveryDetails: delivery.slotDeliveryDetails
            ? {
                ...delivery.slotDeliveryDetails,
                isAutomatic: false,
              }
            : undefined,
        };
      }
      return delivery;
    });

    this._checkoutStore.setDeliveries(newDeliveries);
  }

  updateDelivery(
    selectedSlotId?: string,
    selectedDate?: string,
    isAutomatic?: boolean,
  ) {
    this.setIsLoading(true);

    const { deliveries } = this._checkoutStore;
    const currentDelivery = this.selectedSlot;

    if (!deliveries || !currentDelivery) {
      throw new Error('Something went wrong');
    }

    const currentIndex = deliveries.findIndex(
      (item) => item.id === currentDelivery.id,
    );

    const deliveryIndexToUpdate = deliveries.findIndex(
      ({ slotDeliveryDetails }) =>
        slotDeliveryDetails &&
        selectedDate &&
        slotDeliveryDetails.scheduleSlotId === selectedSlotId &&
        slotDeliveryDetails.currentDate === +selectedDate / 1000,
    );

    const newDeliveries = this._getDeliveries(
      deliveryIndexToUpdate,
      selectedSlotId,
      selectedDate,
      isAutomatic,
    );

    newDeliveries.splice(currentIndex, 1);
    newDeliveries.sort((a, b) => {
      const dateA = a.slotDeliveryDetails?.currentDate;
      const dateB = b.slotDeliveryDetails?.currentDate;

      if (!dateA && !dateB) {
        return 0;
      }

      if (!dateA) {
        return -1;
      }

      if (!dateB) {
        return 1;
      }

      return dateA - dateB;
    });

    runInAction(() => {
      if (
        company.isExpressDeliveryOffByDefault &&
        newDeliveries.length === 1 &&
        !this._checkoutStore.isDeliverTogether
      ) {
        this._checkoutStore.switchDeliverTogether();
        this.setIsAutomatic(false);
      } else {
        this._checkoutStore.setDeliveries(newDeliveries);
      }
    });
  }

  private _getDeliveries(
    indexToUpdate: number,
    selectedSlotId?: string,
    selectedDate?: string,
    isAutomatic?: boolean,
  ): Delivery[] {
    const { deliveries } = this._checkoutStore;
    const currentDelivery = this.selectedSlot;

    if (!deliveries || !currentDelivery) {
      return [];
    }

    if (indexToUpdate >= 0) {
      return deliveries.map((delivery, index) => {
        if (index === indexToUpdate) {
          return {
            ...delivery,
            itemsIds: delivery.itemsIds.concat(currentDelivery.itemsIds),
          };
        }

        return delivery;
      });
    }

    const selectedSlotDeliveryDetails =
      selectedSlotId && selectedDate
        ? {
            scheduleSlotId: selectedSlotId,
            currentDate: +selectedDate / 1000,
            isAutomatic,
          }
        : undefined;
    const lastSelectedSlot = this.getSlotByIdAndDate(
      selectedSlotDeliveryDetails?.scheduleSlotId,
      selectedSlotDeliveryDetails?.currentDate
        ? `${selectedSlotDeliveryDetails?.currentDate}`
        : '',
    );

    return [
      ...deliveries,
      {
        id: uuid(),
        itemsIds: currentDelivery.itemsIds,
        slotDeliveryDetails: selectedSlotDeliveryDetails,
        slot: lastSelectedSlot,
      },
    ];
  }

  getSlotByIdAndDate(
    slotId?: string | null,
    slotDate?: string | null,
  ): DeliverySlot | undefined {
    return slotId && slotDate
      ? this._checkoutStore.sortedDeliverySlotsList?.find(
          ({ schedule_slot_id, current_date }) =>
            schedule_slot_id === slotId && `${current_date}` === slotDate,
        )
      : undefined;
  }

  destroy(): void {
    // Clean up any resources or listeners here if necessary in future
    // Currently, no resources need to be explicitly released
  }
}

export default SlotSelectModalStore;
